import { FormControlLabel, Grid, InputAdornment, MenuItem, Radio, RadioGroup, Select, TextField } from '@mui/material';
import { DISCOUNT_TYPE_OPTIONS, OfferFormData } from './offerForm';
import OfferSection from './OfferSection';
import OptionToggleGroup from './OptionToggleGroup';

interface DiscountSectionProps {
  offer: OfferFormData;
  onChange: (patch: Partial<OfferFormData>) => void;
}

const limitDigits = (value: string, maxDigits: number): string => value.replace(/\D/g, '').slice(0, maxDigits);

const valueFieldSx = { width: { xs: '100%', sm: 220 } };

type RemiseMode = OfferFormData['remiseMode'];

function DiscountDetails({ offer, onChange }: DiscountSectionProps) {
  switch (offer.discountTypeId) {
    case 1:
      return (
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            size="small"
            label="Valeur de la remise"
            type="number"
            value={offer.remiseValue}
            onChange={e => onChange({
              remiseValue: offer.remiseMode === 'pourcentage' ? limitDigits(e.target.value, 3) : e.target.value,
            })}
            slotProps={{
              inputLabel: { shrink: true },
              input: {
                // Seul le symbole de l'unité est affiché après la valeur ; un clic dessus permet de passer de € à %
                endAdornment: (
                  <InputAdornment position="end">
                    <Select
                      variant="standard"
                      disableUnderline
                      value={offer.remiseMode}
                      onChange={e => {
                        const remiseMode = e.target.value as RemiseMode;
                        onChange({
                          remiseMode,
                          ...(remiseMode === 'pourcentage' && { remiseValue: limitDigits(offer.remiseValue, 3) }),
                        });
                      }}
                      inputProps={{ 'aria-label': 'Unité de la remise' }}
                      sx={{ fontWeight: 700, '& .MuiSelect-select': { py: 0, pr: '20px !important', minWidth: 0 } }}
                    >
                      <MenuItem value="montant">€</MenuItem>
                      <MenuItem value="pourcentage">%</MenuItem>
                    </Select>
                  </InputAdornment>
                ),
              },
            }}
            sx={valueFieldSx}
          />
        </Grid>
      );
    case 2:
      return (
        <Grid size={{ xs: 12 }}>
          <RadioGroup
            row
            value={offer.pointsMode}
            onChange={e => onChange({ pointsMode: e.target.value === 'multiplication' ? 'multiplication' : 'ajout' })}
            sx={{ mt: 0.5, mb: 1, flexWrap: 'nowrap' }}
          >
            <FormControlLabel value="ajout" control={<Radio size="small" />} label="Ajout de points" sx={{ whiteSpace: 'nowrap' }} />
            <FormControlLabel value="multiplication" control={<Radio size="small" />} label="Multiplication selon coefficient" sx={{ whiteSpace: 'nowrap' }} />
          </RadioGroup>
          <TextField
            size="small"
            label="Valeur"
            type="number"
            value={offer.pointsValue}
            onChange={e => onChange({ pointsValue: e.target.value })}
            sx={valueFieldSx}
          />
        </Grid>
      );
    case 3:
      return (
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            size="small"
            label="Pourcentage"
            type="number"
            value={offer.remiseValue}
            onChange={e => onChange({ remiseValue: limitDigits(e.target.value, 3), remiseMode: 'pourcentage' })}
            slotProps={{ input: { endAdornment: '%' } }}
            sx={valueFieldSx}
          />
        </Grid>
      );
    default:
      return null;
  }
}

function DiscountSection({ offer, onChange }: DiscountSectionProps) {
  const hasDetails = offer.discountTypeId === 1 || offer.discountTypeId === 2 || offer.discountTypeId === 3;

  return (
    <OfferSection title="Type de remise" dividerTop={3}>
      <OptionToggleGroup<number>
        options={DISCOUNT_TYPE_OPTIONS}
        value={offer.discountTypeId}
        onChange={discountTypeId => onChange({ discountTypeId })}
      />
      {hasDetails && (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <DiscountDetails offer={offer} onChange={onChange} />
        </Grid>
      )}
    </OfferSection>
  );
}

export default DiscountSection;
